/**
 * Where each player sits on the table's clock face.
 *
 * The engine seats players clockwise, so the ring is just that order rotated
 * until the viewer is at 6 o'clock. Their partner then lands at 12 with the
 * opponents alternating down each side, which is the layout the spec asks for.
 */

import type { PlayerView } from "@ryzun/haf-saloon-game-engine";

/**
 * Compass points around the felt, from the viewer's side of the table. The
 * table stylesheet anchors a seat to the rail by its orientation, and a hand
 * is turned so its bottom edge faces out of the table the way it would for
 * someone actually sitting there.
 */
export type SeatOrientation =
    | "east"
    | "north"
    | "northeast"
    | "northwest"
    | "south"
    | "southeast"
    | "southwest"
    | "west";

export interface SeatPlacement {
    readonly player: PlayerView;
    readonly orientation: SeatOrientation;
    /** Clock hour, purely for the accessible description of the table. */
    readonly clock: number;
    /**
     * Which half of the team's tableau this seat anchors: the team's first
     * seat in the viewer-rotated ring shows its melds, the second its books.
     */
    readonly tableauRole: "books" | "melds";
}

const FOUR_SEATS: readonly SeatOrientation[] = ["south", "west", "north", "east"];

const SIX_SEATS: readonly SeatOrientation[] = ["south", "southwest", "northwest", "north", "northeast", "southeast"];

const FOUR_CLOCK: readonly number[] = [6, 9, 12, 3];

const SIX_CLOCK: readonly number[] = [6, 8, 10, 12, 2, 4];

/**
 * Rotates the seating order so the viewer comes first, preserving the clockwise
 * order the engine deals and takes turns in.
 */
export function rotateToViewer(
    players: readonly PlayerView[],
    viewerId: string
): readonly PlayerView[] {
    const start = players.findIndex((player) => player.id === viewerId);

    if (start <= 0) {
        return players;
    }

    return [...players.slice(start), ...players.slice(0, start)];
}

export function placeSeats(players: readonly PlayerView[], viewerId: string): readonly SeatPlacement[] {
    const ring = rotateToViewer(players, viewerId);
    const orientations = ring.length === 4 ? FOUR_SEATS : SIX_SEATS;
    const clock = ring.length === 4 ? FOUR_CLOCK : SIX_CLOCK;

    /**
     * Teams are assigned round-robin by seat index, so in this viewer-rotated
     * ring the first `teamCount` entries are one member per team - the viewer
     * among them - and the rest are their partners (see getTeamId on the
     * server). That split is what decides whose melds versus books render in
     * front of them.
     */
    const teamCount = new Set(ring.map((player) => player.teamId)).size;

    return ring.map((player, index) => ({
        player,
        orientation: orientations[index] ?? "south",
        clock: clock[index] ?? 6,
        tableauRole: index < teamCount ? "melds" : "books"
    }));
}

/** The viewer's partner, or null at a table where nobody shares their team. */
export function findPartner(players: readonly PlayerView[], viewerId: string): PlayerView | null {
    const self = players.find((player) => player.id === viewerId);

    if (self === undefined) {
        return null;
    }

    return players.find((player) => player.id !== viewerId && player.teamId === self.teamId) ?? null;
}
